/**
 * HeaderCard
 *
 * Renders the branded header / hero strip used across public-facing booking and
 * branding flows, with support for multiple header variations.
 * Used in `BookingPage`, `AppointmentCancelPage`, `StyleYourBusinessPage`, and
 * `BookingFormTab`, while `App.tsx` separately mounts the top app-shell header.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import { HeaderCardElements, HeaderCardVariationTypes } from './HeaderCardElements';
import Image from 'react-bootstrap/Image';
import { CLASSIC } from '../../styling/ColorSchemes';
import { FONT_THEME } from '../../styling/FontSchemes';
import { useStyle } from '../../contexts/StyleContext';
import { useScreenSize } from '../../contexts/ScreenSizeContext';
import { SHADOWS, SPACING } from '../../styling/DesignTokens';

const HEADER_BACKGROUND_VARIATION_FOLDER: Partial<Record<HeaderCardVariationTypes, string>> = {
  [HeaderCardVariationTypes.STRIPE]: 'Stripe',
  [HeaderCardVariationTypes.ABSTRACT]: 'Abstract',
  [HeaderCardVariationTypes.CHECKERED]: 'Checkered',
};

/** Schemes with a dark PRIMARY color that need a light-coloured texture overlay. */
const DARK_PRIMARY_SCHEME_NAMES = new Set([
  'DARK', 'BLOOD_RED', 'FOREST_GREEN', 'MIDNIGHT_NAVY', 'CHARCOAL_STEEL', 'DEEP_BURGUNDY',
]);

/** Shared single-file asset name per variation folder (overrides per-scheme lookup). */
const HEADER_BACKGROUND_SHARED_ASSET: Partial<Record<HeaderCardVariationTypes, string>> = {
  [HeaderCardVariationTypes.ABSTRACT]: 'Abstract',
};

const HEADER_BACKGROUND_SCHEME_ASSET: Record<string, string> = {};

const HEADER_BACKGROUND_FALLBACK_ASSETS: string[] = [];

export default function HeaderCard(props: HeaderCardElements) {
  const { colorScheme: contextColorScheme, fontScheme: contextFontScheme } = useStyle();
  const { width } = useScreenSize();
  const {
    title,
    variation = HeaderCardVariationTypes.CLASSIC,
    colorScheme = contextColorScheme ?? CLASSIC,
    fontScheme = contextFontScheme ?? FONT_THEME.CLASSIC,
    isFixed = true,
    padding = 'clamp(0.75rem, 1.8vw, 1rem) clamp(1rem, 3vw, 2rem)',
    brandHref = '/',
    // showBrandDetails removed - subtitle now hidden everywhere
    showGetStartedButton = false,
    onGetStartedClick,
  } = props;
  const backgroundVariationFolder = HEADER_BACKGROUND_VARIATION_FOLDER[variation];
  const backgroundPathCandidates = useMemo(() => {
    if (!backgroundVariationFolder) {
      return [];
    }

    const publicUrl = process.env.PUBLIC_URL ?? '';
    const isDark = DARK_PRIMARY_SCHEME_NAMES.has(colorScheme.NAME);
    const checkeredAsset = isDark ? 'Checkered-Light' : 'Checkered';
    const stripesAsset = isDark ? 'Stripes-Light' : 'Stripes';
    const sharedAsset = variation === HeaderCardVariationTypes.CHECKERED
      ? checkeredAsset
      : variation === HeaderCardVariationTypes.STRIPE
        ? stripesAsset
        : HEADER_BACKGROUND_SHARED_ASSET[variation];
    const assetCandidates = sharedAsset
      ? [sharedAsset]
      : Array.from(new Set([
          HEADER_BACKGROUND_SCHEME_ASSET[colorScheme.NAME],
          colorScheme.NAME,
          ...HEADER_BACKGROUND_FALLBACK_ASSETS,
        ].filter((assetName): assetName is string => Boolean(assetName))));

    return assetCandidates.map((assetName) => (
      `${publicUrl}/images/BannerBackgrounds/${backgroundVariationFolder}/${assetName}.png`
    ));
  }, [backgroundVariationFolder, variation, colorScheme.NAME]);

  const navRef = useRef<HTMLElement | null>(null);
  const [spacerH, setSpacerH] = useState<number>(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [resolvedBackgroundPath, setResolvedBackgroundPath] = useState<string | null>(backgroundPathCandidates[0] ?? null);

  // useLayoutEffect runs synchronously after DOM mutations but before browser paint,
  // so spacerH is set to the real header height before the first visible frame —
  // this prevents the 0px→64px jump that causes CLS on the landing page.
  useLayoutEffect(() => {
    if (!isFixed) return;
    const update = () => setSpacerH(navRef.current ? navRef.current.offsetHeight || 0 : 0);
    update();
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', onScroll);
    };
  }, [isFixed]);

  useEffect(() => {
    if (backgroundPathCandidates.length === 0) {
      setResolvedBackgroundPath(null);
      return;
    }

    let isMounted = true;

    const resolveImagePath = async () => {
      for (const candidatePath of backgroundPathCandidates) {
        const image = new window.Image();
        const didLoad = await new Promise<boolean>((resolve) => {
          image.onload = () => resolve(true);
          image.onerror = () => resolve(false);
          image.src = candidatePath;
        });

        if (didLoad) {
          if (isMounted) {
            setResolvedBackgroundPath(candidatePath);
          }
          return;
        }
      }

      if (isMounted) {
        setResolvedBackgroundPath(null);
      }
    };

    void resolveImagePath();

    return () => {
      isMounted = false;
    };
  }, [backgroundPathCandidates]);

  const isImageVariation = variation === HeaderCardVariationTypes.STRIPE
    || variation === HeaderCardVariationTypes.ABSTRACT
    || variation === HeaderCardVariationTypes.CHECKERED;
  const isDarkStripe = isImageVariation && DARK_PRIMARY_SCHEME_NAMES.has(colorScheme.NAME);

  const headerShellBackground = (() => {
    const gradientOverlayOpacity = colorScheme.GRADIENT_OVERLAY_OPACITY ?? 0.58;
    const accessibleGradient = colorScheme.GRADIENT
      ? `linear-gradient(rgba(15, 23, 42, ${gradientOverlayOpacity}), rgba(15, 23, 42, ${gradientOverlayOpacity})), ${colorScheme.GRADIENT}`
      : null;

    switch (variation) {
      case HeaderCardVariationTypes.BOLD_GRADIENT:
        return accessibleGradient
          ?? `linear-gradient(135deg, ${colorScheme.ACCENT} 0%, ${colorScheme.PRIMARY} 35%, ${colorScheme.PRIMARY}EE 55%, ${colorScheme.PRIMARY}AA 75%, ${colorScheme.SECONDARY}66 100%)`;

      case HeaderCardVariationTypes.MINIMAL:
        return 'none';

      case HeaderCardVariationTypes.SOLID:
        return colorScheme.PRIMARY;

      default:
        if (colorScheme.GRADIENT) {
          if (variation === HeaderCardVariationTypes.CLASSIC) {
            return `linear-gradient(rgba(255, 255, 255, 0.72), rgba(255, 255, 255, 0.72)), ${colorScheme.GRADIENT}`;
          }
          if (isImageVariation && resolvedBackgroundPath) {
            return [
              `linear-gradient(rgba(15, 23, 42, ${gradientOverlayOpacity}), rgba(15, 23, 42, ${gradientOverlayOpacity}))`,
              `url(${resolvedBackgroundPath})`,
              colorScheme.GRADIENT,
            ].join(', ');
          }
          return accessibleGradient;
        }

        // CLASSIC, STRIPE, ABSTRACT, CHECKERED — image-backed or gradient fallback
        if ((variation === HeaderCardVariationTypes.STRIPE || variation === HeaderCardVariationTypes.ABSTRACT || variation === HeaderCardVariationTypes.CHECKERED) && resolvedBackgroundPath) {
          return `url(${resolvedBackgroundPath})`;
        }
        return resolvedBackgroundPath
          ? [
            `linear-gradient(135deg, ${colorScheme.WHITE}B8 0%, ${colorScheme.BACKGROUND}82 52%, ${colorScheme.SECONDARY}66 100%)`,
            `radial-gradient(circle at top right, ${colorScheme.PRIMARY}16 0%, transparent 48%)`,
            `url(${resolvedBackgroundPath})`,
          ].join(', ')
          : [
            `linear-gradient(135deg, ${colorScheme.WHITE}F2 0%, ${colorScheme.BACKGROUND}E8 52%, ${colorScheme.SECONDARY}D4 100%)`,
            `radial-gradient(circle at top right, ${colorScheme.PRIMARY}22 0%, transparent 48%)`,
          ].join(', ');
    }
  })();

  const useLightText = variation === HeaderCardVariationTypes.BOLD_GRADIENT
    || variation === HeaderCardVariationTypes.SOLID
    || isImageVariation;

  const isWhiteBackground = variation === HeaderCardVariationTypes.MINIMAL || variation === HeaderCardVariationTypes.CLASSIC;
  const titleColor = useLightText ? '#FFFFFF' : (isWhiteBackground ? colorScheme.PRIMARY : colorScheme.TEXT_PRIMARY);
  // subtitleColor removed - subtitle now hidden everywhere
  const borderBottomStyle = variation === HeaderCardVariationTypes.MINIMAL
    ? `2px solid ${colorScheme.PRIMARY}`
    : variation === HeaderCardVariationTypes.SOLID
      ? `1px solid ${colorScheme.ACCENT}`
      : `1px solid ${isScrolled ? colorScheme.BORDER : `${colorScheme.BORDER}B8`}`;
  const backgroundSize = 'cover';
  const backgroundPosition = 'center';

  return (
    <>
      <Navbar
        ref={navRef}
        style={{
          backgroundColor: (variation === HeaderCardVariationTypes.SOLID || isImageVariation)
            ? (isDarkStripe && colorScheme.PRIMARY === '#000000'
                  ? colorScheme.SECONDARY
                  : colorScheme.PRIMARY)
            : colorScheme.WHITE,
          backgroundImage: variation === HeaderCardVariationTypes.SOLID
            ? 'none'
            : headerShellBackground ?? undefined,
          backgroundBlendMode: colorScheme.GRADIENT && isImageVariation
            ? 'normal'
            : isDarkStripe && colorScheme.PRIMARY !== '#000000'
              ? 'screen'
              : undefined,
          backgroundSize,
          backgroundPosition,
          backgroundRepeat: 'no-repeat',
          backdropFilter: (variation === HeaderCardVariationTypes.SOLID || isImageVariation) ? 'none' : 'blur(18px)',
          WebkitBackdropFilter: (variation === HeaderCardVariationTypes.SOLID || isImageVariation) ? 'none' : 'blur(18px)',
          borderBottom: borderBottomStyle,
          boxShadow: isScrolled
            ? `${SHADOWS.MD}, inset 0 1px 0 rgba(255, 255, 255, 0.7)`
            : 'inset 0 1px 0 rgba(255, 255, 255, 0.62)',
          padding: width < 576 ? 'clamp(0.25rem, 1vw, 0.35rem) clamp(0.75rem, 2vw, 1rem)' : 'clamp(0.5rem, 1.4vw, 0.75rem) clamp(1rem, 3vw, 2rem)',
          minHeight: width < 576 ? '54px' : '70px',
          position: isFixed ? 'fixed' as const : 'relative' as const,
          top: isFixed ? 'env(safe-area-inset-top, 0px)' : undefined,
          left: isFixed ? 0 : undefined,
          right: isFixed ? 0 : undefined,
          width: '100%',
          zIndex: isFixed ? 1000 : 'auto',
          transition: 'box-shadow 200ms ease, border-color 200ms ease',
        }}
        variant="light"
      >
        <Container
          fluid
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 'inherit',
            maxWidth: 'none',
            padding,
            position: 'relative',
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: SPACING.LG,
              padding: `0.2rem 0`,
              position: 'relative',
            }}
          >
            <Navbar.Brand
              as="a"
              href={brandHref ?? undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING.MD,
                margin: 0,
                flex: 1,
                minWidth: 0,
                position: 'relative',
                zIndex: 1,
                cursor: brandHref ? 'pointer' : 'default',
                textDecoration: 'none',
              }}
            >
              {props.logoSrc && (
                <Image
                  src={props.logoSrc}
                  alt="Logo"
                  width={76}
                  height={76}
                  style={{
                    height: width < 576 ? 'clamp(40px, 6vw, 56px)' : 'clamp(38px, 4.5vw, 52px)',
                    width: width < 576 ? 'clamp(40px, 6vw, 56px)' : 'clamp(38px, 4.5vw, 52px)',
                    objectFit: 'contain',
                    border: 'none',
                    display: 'block',
                    flexShrink: 0,
                  }}
                  fluid
                />
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.14rem' }}>
                {/* Removed "Modern booking platform" subtitle */}

                <span
                  style={{
                    fontSize: width < 576 ? 'clamp(0.95rem, 2.2vw, 1.2rem)' : 'clamp(1.05rem, 2.4vw, 1.72rem)',
                    fontWeight: 700,
                    lineHeight: 1.08,
                    color: titleColor,
                    fontFamily: fontScheme.HEADING,
                    whiteSpace: width < 576 ? 'normal' : 'nowrap',
                    overflowWrap: 'anywhere',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {title}
                </span>
              </div>
            </Navbar.Brand>

            {showGetStartedButton && (
              <button
                onClick={onGetStartedClick}
                style={{
                  padding: '0.6rem 1rem',
                  borderRadius: '6px',
                  background: `linear-gradient(135deg, ${colorScheme.PRIMARY} 0%, ${colorScheme.ACCENT} 100%)`,
                  border: 'none',
                  color: colorScheme.WHITE,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  fontFamily: fontScheme.BODY,
                  cursor: 'pointer',
                  boxShadow: SHADOWS.MD,
                  transition: 'all 200ms ease',
                  whiteSpace: 'nowrap',
                  marginBottom: 'auto',
                  alignSelf: 'flex-end',
                  marginTop: 'auto',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(-2px)';
                  (e.target as HTMLButtonElement).style.boxShadow = SHADOWS.LG;
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(0)';
                  (e.target as HTMLButtonElement).style.boxShadow = SHADOWS.MD;
                }}
              >
                Get Started
              </button>
            )}

          </div>
        </Container>
      </Navbar>
      {isFixed ? <div style={{ height: spacerH }} /> : null}
    </>
  );
}
